/**
 * Guide Complet - Documentation Fusionnée
 *
 * Combine:
 * - Démarrage Rapide (onboarding)
 * - Documentation Complète (fonctionnalités)
 * - Feuille de Route (développements futurs)
 * - Explications des Bots et Dashboards
 *
 * Professionnel, convivial et complet
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';

type Tab = 'getting-started' | 'features' | 'bots-dashboards' | 'autopilot' | 'advanced' | 'roadmap' | 'faq';

export function ComprehensiveGuide() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('getting-started');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const tabs = [
    { id: 'getting-started', label: t('guide.tabs.gettingStarted'), emoji: '🚀' },
    { id: 'features', label: t('guide.tabs.features'), emoji: '💎' },
    { id: 'bots-dashboards', label: t('guide.tabs.botsDashboards'), emoji: '🤖' },
    { id: 'autopilot', label: t('guide.tabs.autopilot'), emoji: '✈️' },
    { id: 'advanced', label: t('guide.tabs.advanced'), emoji: '🔧' },
    { id: 'roadmap', label: t('guide.tabs.roadmap'), emoji: '🗺️' },
    { id: 'faq', label: t('guide.tabs.faq'), emoji: '❓' }
  ];

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '60px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Section Hero */}
      <div className="card" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        marginBottom: '24px',
        padding: '40px 32px'
      }}>
        <h1 style={{ fontSize: '36px', marginBottom: '16px', fontWeight: 800 }}>
          📚 {t('guide.title')}
        </h1>
        <p style={{ fontSize: '18px', opacity: 0.95, lineHeight: '1.7', maxWidth: '800px' }}>
          {t('guide.subtitle')}
        </p>
        <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 500
          }}>
            ✨ {t('guide.badges.friendly')}
          </span>
          <span style={{
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 500
          }}>
            📱 {t('guide.badges.mobile')}
          </span>
          <span style={{
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 500
          }}>
            🔐 {t('guide.badges.secure')}
          </span>
        </div>
      </div>

      {/* Introduction Cards - Swiss Army Knife + Story - 2 columns */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: '20px',
        marginBottom: '24px'
      }}>
        {/* Swiss Army Knife Card */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              padding: '12px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: '12px'
            }}>
              <Icons.Package size={24} style={{ color: 'white' }} />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700 }}>
              🔧 {t('guide.intro.swissArmyKnife.title')}
            </h2>
          </div>
          <p style={{ color: 'var(--muted)', fontSize: '14px', lineHeight: 1.7, marginBottom: '20px' }}>
            {t('guide.intro.swissArmyKnife.description')}
          </p>
          <div style={{ display: 'grid', gap: '12px' }}>
            {[
              { key: 'modular', icon: Icons.Layers, color: '#6366f1' },
              { key: 'independent', icon: Icons.Activity, color: '#10b981' },
              { key: 'customizable', icon: Icons.Settings, color: '#f59e0b' }
            ].map(item => (
              <div key={item.key} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '12px',
                background: 'var(--bg)',
                borderRadius: '8px',
                borderLeft: `3px solid ${item.color}`
              }}>
                <item.icon size={20} style={{ color: item.color, flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                    {t(`guide.intro.swissArmyKnife.features.${item.key}.title`)}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                    {t(`guide.intro.swissArmyKnife.features.${item.key}.description`)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Story Card */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              padding: '12px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '12px'
            }}>
              <Icons.Heart size={24} style={{ color: 'white' }} />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700 }}>
              💡 {t('guide.intro.story.title')}
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <p style={{ color: 'var(--muted)', fontSize: '14px', lineHeight: 1.7 }}>
              {t('guide.intro.story.description')}
            </p>
            <p style={{ color: 'var(--muted)', fontSize: '14px', lineHeight: 1.7 }}>
              {t('guide.intro.story.openSource')}
            </p>
            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
              borderRadius: '8px',
              borderLeft: '3px solid #6366f1'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Icons.Shield size={18} style={{ color: '#6366f1' }} />
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#6366f1' }}>Ma philosophie</span>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text)', margin: 0 }}>
                {t('guide.intro.story.philosophy')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation par Onglets */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        background: 'var(--card)',
        padding: '10px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(20,30,50,0.06)'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as Tab)}
            style={{
              flex: '1 1 auto',
              minWidth: '120px',
              padding: '10px 16px',
              background: activeTab === tab.id ? 'var(--accent)' : 'transparent',
              color: activeTab === tab.id ? 'white' : 'var(--text)',
              border: activeTab === tab.id ? 'none' : '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            {tab.emoji} {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu des Onglets */}
      {activeTab === 'getting-started' && <GettingStartedContent />}
      {activeTab === 'features' && <FeaturesContent />}
      {activeTab === 'bots-dashboards' && <BotsDashboardsContent />}
      {activeTab === 'autopilot' && <AutopilotTradingContent />}
      {activeTab === 'advanced' && <AdvancedContent />}
      {activeTab === 'roadmap' && <RoadmapContent />}
      {activeTab === 'faq' && <FAQContent expandedSection={expandedSection} toggleSection={toggleSection} />}
    </div>
  );
}

// ==================== DÉMARRAGE RAPIDE ====================
function GettingStartedContent() {
  const { t } = useTranslation();

  const steps = [
    { num: 1, color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', key: 'step1' },
    { num: 2, color: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', key: 'step2' },
    { num: 3, color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', key: 'step3' }
  ];

  const features = [
    { icon: '📊', key: 'analytics' },
    { icon: '🎯', key: 'modes' },
    { icon: '📱', key: 'mobile' },
    { icon: '🔐', key: 'privacy' },
    { icon: '📥📤', key: 'exportImport' },
    { icon: '🔔', key: 'alerts' },
    { icon: '🌙', key: 'darkMode' }
  ];

  const shortcuts = [
    { keys: '⌘K / Ctrl+K', actionKey: 'commandPalette' },
    { keys: '⌘↑ / Ctrl+↑', actionKey: 'previousPage' },
    { keys: '⌘↓ / Ctrl+↓', actionKey: 'nextPage' },
    { keys: 'A', actionKey: 'addPosition' },
    { keys: 'R', actionKey: 'refreshPrices' },
    { keys: 'E', actionKey: 'exportPortfolio' },
    { keys: 'C', actionKey: 'toggleCompact' },
    { keys: 'Shift+H / ?', actionKey: 'showHelp' },
    { keys: '/', actionKey: 'quickSearch' },
    { keys: 'Esc', actionKey: 'closeModals' }
  ];

  return (
    <>
      {/* 2-column layout for Quick Start and Features */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '20px',
        marginBottom: '20px'
      }}>
        {/* Démarrage Rapide */}
        <div className="card">
          <h2 style={{ fontSize: '22px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.TrendingUp size={22} />
            {t('guide.gettingStarted.title')}
          </h2>

          <div style={{ display: 'grid', gap: '16px' }}>
            {steps.map(step => (
              <div key={step.key} style={{
                display: 'flex',
                gap: '16px',
                padding: '16px',
                background: 'var(--bg)',
                borderRadius: '10px',
                borderLeft: `4px solid ${step.color}`
              }}>
                <div style={{
                  minWidth: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: step.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>
                  {step.num}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '8px', fontWeight: 600 }}>
                    {t(`guide.gettingStarted.${step.key}.title`)}
                  </h3>
                  <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.6', marginBottom: '10px' }}>
                    {t(`guide.gettingStarted.${step.key}.description`)}
                  </p>
                  <div style={{
                    padding: '10px 12px',
                    background: `${step.color}15`,
                    borderRadius: '6px',
                    borderLeft: `2px solid ${step.color}`,
                    fontSize: '12px'
                  }}>
                    <strong style={{ color: step.color }}>💡</strong> {t(`guide.gettingStarted.${step.key}.tip`)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fonctionnalités Essentielles */}
        <div className="card">
          <h2 style={{ fontSize: '22px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.Sparkles size={22} />
            {t('guide.gettingStarted.essentialFeatures.title')}
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            {features.map((feature, idx) => (
              <div key={idx} style={{
                padding: '14px',
                background: 'var(--bg)',
                borderRadius: '10px',
                border: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>{feature.icon}</div>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
                  {t(`guide.gettingStarted.essentialFeatures.${feature.key}.title`)}
                </h3>
                <p style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: '1.5' }}>
                  {t(`guide.gettingStarted.essentialFeatures.${feature.key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Raccourcis Clavier - Full width */}
      <div className="card">
        <h2 style={{ fontSize: '22px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Keyboard size={22} />
          ⌨️ {t('guide.gettingStarted.shortcuts.title')}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
          {shortcuts.map((shortcut, idx) => (
            <div key={idx} style={{
              padding: '10px 12px',
              background: 'var(--bg)',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '13px'
            }}>
              <code style={{
                padding: '4px 8px',
                background: 'var(--card)',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--accent)'
              }}>
                {shortcut.keys}
              </code>
              <span style={{ color: 'var(--muted)', fontSize: '12px' }}>
                {t(`guide.gettingStarted.shortcuts.${shortcut.actionKey}`)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ==================== FONCTIONNALITÉS ====================
function FeaturesContent() {
  const { t } = useTranslation();
  const portfolioFeatures = t('guide.features.portfolioManagement.items', { returnObjects: true }) as string[];
  const analyticsFeatures = t('guide.features.analytics.items', { returnObjects: true }) as string[];
  const securityFeatures = t('guide.features.security.items', { returnObjects: true }) as string[];

  return (
    <>
      {/* 2-column layout for features */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '20px',
        marginBottom: '20px'
      }}>
        {/* Gestion de Portfolio */}
        <div className="card">
          <h2 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.Package size={22} />
            💼 {t('guide.features.portfolioManagement.title')}
          </h2>
          <div style={{ display: 'grid', gap: '8px' }}>
            {(Array.isArray(portfolioFeatures) ? portfolioFeatures : []).map((feature, idx) => (
              <div key={idx} style={{
                padding: '10px 12px',
                background: 'var(--bg)',
                borderRadius: '6px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ color: '#10b981', fontWeight: 'bold' }}>✓</span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Analytics & Security Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Analytics */}
          <div className="card">
            <h2 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Icons.BarChart size={22} />
              📊 {t('guide.features.analytics.title')}
            </h2>
            <div style={{ display: 'grid', gap: '8px' }}>
              {(Array.isArray(analyticsFeatures) ? analyticsFeatures : []).map((feature, idx) => (
                <div key={idx} style={{
                  padding: '10px 12px',
                  background: 'var(--bg)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ color: '#6366f1', fontWeight: 'bold' }}>✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Security */}
          <div className="card">
            <h2 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Icons.Shield size={22} />
              🔐 {t('guide.features.security.title')}
            </h2>
            <div style={{ display: 'grid', gap: '8px' }}>
              {(Array.isArray(securityFeatures) ? securityFeatures : []).map((feature, idx) => (
                <div key={idx} style={{
                  padding: '10px 12px',
                  background: 'var(--bg)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Features - Full width card */}
      <div className="card">
        <h2 style={{ fontSize: '22px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Zap size={22} />
          🤖 Trading & Automatisation
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '8px' }}>
          {[
            'Suivi Bots KuCoin (Grid, DCA, Infinity Grid, Margin Grid)',
            'Intégration Crypto.com (Staking, Trading, Carte)',
            'Monitoring performance bots en temps réel',
            'Suivi frais et calcul ROI',
            'Système d\'alertes (alertes prix, seuils déclencheurs)',
            'Alertes Intelligentes avec recommandations IA',
            'Mode Autopilot pour décisions automatisées',
            'Simulateur What-If pour planification scénarios'
          ].map((feature, idx) => (
            <div key={idx} style={{
              padding: '10px 12px',
              background: 'var(--bg)',
              borderRadius: '6px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>✓</span>
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* FluxNode Monitor */}
      <div className="card">
        <h2 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Server size={22} />
          🖥️ FluxNode Monitor
        </h2>
        <div style={{ display: 'grid', gap: '8px' }}>
          {[
            'Surveillance multi-wallet Flux/Zelcore en temps réel',
            'Support 3 tiers de nodes: CUMULUS (1K), NIMBUS (12.5K), STRATUS (40K)',
            'Support adresses t1 (standard) et t3 (multisig SSP Wallet)',
            'Monitoring statut nodes: CONFIRMED, OFFLINE, EXPIRED, DOS_BASE',
            'Dashboard agrégé: Total Value Locked (TVL), revenus journaliers/mensuels',
            'Projections de récompenses avec prix FLUX en temps réel (CoinGecko)',
            'Calcul ROI automatique par tier et par node',
            'Auto-refresh 60s pour nodes, 2min pour prix FLUX',
            'Tracking des applications déployées sur Flux Cloud (dApps)',
            'Recherche d\'apps par adresse owner (ZelID, Bitcoin, Ethereum)',
            'Vue détaillée des ressources utilisées (CPU, RAM, Storage)'
          ].map((feature, idx) => (
            <div key={idx} style={{
              padding: '10px 12px',
              background: 'var(--bg)',
              borderRadius: '6px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>✓</span>
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Modules - 2 column layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '20px',
        marginBottom: '20px'
      }}>
        {/* Module Hub */}
        <div className="card">
          <h2 style={{ fontSize: '18px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.Grid size={20} />
            🎛️ Module Hub
          </h2>
          <div style={{ display: 'grid', gap: '6px' }}>
            {[
              'Gestion modulaire: activez/désactivez les pages',
              'Quick Settings: toggles Dark/Light et Simple/Expert',
              'Backup/Restore: préférences incluses dans export JSON'
            ].map((feature, idx) => (
              <div key={idx} style={{ padding: '8px 10px', background: 'var(--bg)', borderRadius: '6px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>✓</span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile & UX */}
        <div className="card">
          <h2 style={{ fontSize: '18px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.Layout size={20} />
            📱 Mobile & UX
          </h2>
          <div style={{ display: 'grid', gap: '6px' }}>
            {[
              'Design responsive (iPhone, iPad, Android)',
              'Support PWA installable comme app',
              'Mode sombre avec transitions fluides',
              'Palette Commandes (⌘K) pour actions rapides'
            ].map((feature, idx) => (
              <div key={idx} style={{ padding: '8px 10px', background: 'var(--bg)', borderRadius: '6px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>✓</span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cost Basis Chain Tracking - NEW FEATURE */}
      <div className="card" style={{ border: '2px solid var(--accent)', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <span style={{
            padding: '6px 12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 600
          }}>
            🆕 NOUVEAU v1.4
          </span>
          <h2 style={{ fontSize: '24px', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.TrendingUp size={24} />
            Cost Basis Chain Tracking
          </h2>
        </div>

        <p style={{ fontSize: '15px', color: 'var(--muted)', marginBottom: '20px', lineHeight: '1.7' }}>
          Suivez la <strong>vraie performance</strong> de vos investissements à travers plusieurs swaps crypto-crypto.
          Le système préserve votre coût d'acquisition initial même après des dizaines de swaps !
        </p>

        {/* UNIQUE Feature Banner */}
        <div style={{
          marginBottom: '24px',
          padding: '20px 24px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
          borderRadius: '12px',
          border: '2px solid var(--accent)',
          boxShadow: '0 4px 20px rgba(139, 92, 246, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '32px' }}>🏆</span>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: 'var(--accent)' }}>
                Fonctionnalité EXCLUSIVE - Introuvable ailleurs !
              </h3>
            </div>
          </div>
          <p style={{ fontSize: '15px', lineHeight: '1.8', margin: 0, color: 'var(--text)' }}>
            <strong>Edouard.ai est le SEUL tracker crypto au monde</strong> à calculer automatiquement votre performance réelle à travers les chaînes de swaps.
            CoinGecko, CoinMarketCap, Delta, Blockfolio, FTX — <strong>aucun concurrent n'offre cette fonctionnalité</strong>.
            <br/><br/>
            Résultat ? Vous voyez <strong>DEUX performances</strong> côte à côte : celle depuis le swap (trompeuse) ET celle depuis votre investissement initial (la vérité).
          </p>
        </div>

        {/* Example scenario */}
        <div style={{ marginBottom: '24px', padding: '20px', background: 'rgba(99, 102, 241, 0.05)', borderRadius: '12px', borderLeft: '4px solid var(--accent)' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '12px', fontWeight: 600 }}>
            💡 Exemple Concret
          </h3>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <div style={{ marginBottom: '8px' }}>📌 <strong>Achat initial :</strong> 1 BTC à 20,000€</div>
            <div style={{ marginBottom: '8px' }}>📈 <strong>BTC monte :</strong> 24,000€ (+20%)</div>
            <div style={{ marginBottom: '8px' }}>🔄 <strong>Swap BTC → ETH</strong></div>
            <div style={{ marginBottom: '8px' }}>📉 <strong>ETH baisse :</strong> 21,600€</div>
            <div style={{ marginTop: '16px', padding: '12px', background: 'var(--bg)', borderRadius: '8px' }}>
              <div style={{ marginBottom: '6px' }}>❌ <strong>Sans tracking :</strong> Performance affichée = -10% (trompeur)</div>
              <div>✅ <strong>Avec tracking :</strong> Performance réelle = +8% (depuis 20,000€)</div>
            </div>
          </div>
        </div>

        {/* Features list */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: 'var(--accent)' }}>
            ✨ Fonctionnalités Incluses
          </h3>
          <div style={{ display: 'grid', gap: '16px' }}>
            {[
              {
                icon: '📊',
                title: 'Dual P/L Display',
                desc: 'Affiche DEUX performances : token actuel (-10%) ET origine (+8%) côte à côte'
              },
              {
                icon: '🗺️',
                title: 'Timeline Interactive',
                desc: 'Visualisation complète de votre chaîne de swaps avec performances à chaque étape'
              },
              {
                icon: '🔄',
                title: 'Navigation Swap Chain',
                desc: 'Boutons prev/next pour naviguer dans l\'historique complet de vos swaps'
              },
              {
                icon: '📈',
                title: 'Graphique Performance Origine',
                desc: 'Courbe historique montrant l\'évolution depuis l\'investissement initial'
              },
              {
                icon: '💰',
                title: 'Export Fiscal Form 2086',
                desc: 'Rapport fiscal détaillé conforme France avec traçabilité des swaps'
              },
              {
                icon: '♾️',
                title: 'Chaînes Illimitées',
                desc: 'Support de swaps multiples : BTC → ETH → HBAR → SOL → MATIC → ...'
              }
            ].map((feature, idx) => (
              <div key={idx} style={{
                padding: '16px',
                background: 'var(--bg)',
                borderRadius: '8px',
                display: 'flex',
                gap: '16px',
                alignItems: 'start'
              }}>
                <span style={{ fontSize: '28px', flexShrink: 0 }}>{feature.icon}</span>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '6px' }}>{feature.title}</div>
                  <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.6' }}>{feature.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: 'var(--accent)' }}>
            🔧 Comment ça Fonctionne
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            {[
              '1️⃣ Lors d\'un swap, le système calcule votre coût d\'acquisition initial (FIFO)',
              '2️⃣ Le coût original est préservé et propagé au nouveau token',
              '3️⃣ Un ID unique de chaîne (chain_id) lie tous les swaps ensemble',
              '4️⃣ Chaque position conserve le lien vers sa position source',
              '5️⃣ Les performances sont calculées par rapport aux DEUX références',
              '6️⃣ Export/Import JSON préserve automatiquement toutes les données'
            ].map((step, idx) => (
              <div key={idx} style={{
                padding: '12px 16px',
                background: 'var(--bg)',
                borderRadius: '6px',
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                {step}
              </div>
            ))}
          </div>
        </div>

        {/* MiCA Compliance */}
        <div style={{ padding: '16px', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '8px', fontWeight: 600, color: '#10b981' }}>
            ✅ Conforme Réglementation MiCA
          </h3>
          <p style={{ fontSize: '14px', lineHeight: '1.7', margin: 0, color: 'var(--muted)' }}>
            Les swaps crypto-crypto ne sont <strong>PAS taxables</strong> selon la réglementation MiCA (EU).
            Seules les conversions vers fiat (EUR, USD) déclenchent l'imposition. Le système calcule
            automatiquement votre plus-value fiscale réelle en tenant compte de toute la chaîne de swaps.
          </p>
        </div>
      </div>

      {/* Gestion des Données */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Database size={24} />
          Gestion des Données & Confidentialité
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #10b981' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 600 }}>
              🔐 100% Confidentialité
            </h3>
            <ul style={{ fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px', margin: 0 }}>
              <li>Toutes les données stockées dans le navigateur (localStorage)</li>
              <li>Pas de backend, pas de base de données, pas de tracking</li>
              <li>Aucun compte requis</li>
              <li>Aucune collecte de données personnelles</li>
              <li>Clés API chiffrées localement</li>
            </ul>
          </div>

          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #6366f1' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 600 }}>
              📤 Options d'Export
            </h3>
            <ul style={{ fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px', margin: 0 }}>
              <li>Format JSON (sauvegarde complète avec prix)</li>
              <li>Format CSV (compatible Excel)</li>
              <li>QR Code (transfert mobile)</li>
              <li>Export rapport fiscal</li>
              <li>Inclut tous les caches et paramètres</li>
            </ul>
          </div>

          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #f59e0b' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 600 }}>
              📥 Options d'Import
            </h3>
            <ul style={{ fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px', margin: 0 }}>
              <li>Import fichier JSON (drag & drop)</li>
              <li>Scanner QR Code (caméra)</li>
              <li>Restauration automatique données</li>
              <li>Récupération cache prix</li>
              <li>Fusionner ou remplacer données existantes</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Export Fiscal MiCA */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.FileText size={24} />
          📋 Export Fiscal - Conformité MiCA
        </h2>

        <div style={{
          padding: '20px 24px',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)',
          borderRadius: '12px',
          border: '2px solid #10b981',
          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.2)',
          marginBottom: '24px'
        }}>
          <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 700, color: '#10b981' }}>
            🏛️ Préparé pour la Réglementation MiCA (Markets in Crypto-Assets)
          </h3>
          <p style={{ fontSize: '14px', lineHeight: '1.8', margin: 0 }}>
            <strong>Edouard.ai</strong> est le <strong>SEUL tracker crypto</strong> à inclure TOUTES les métadonnées
            nécessaires pour la conformité MiCA dans l'export fiscal. Les données de vos swaps, mining et staking
            sont <strong>entièrement tracées</strong> et prêtes pour vos déclarations fiscales.
          </p>
        </div>

        {/* What's Included */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: 'var(--accent)' }}>
            📊 Ce qui est Inclus dans l'Export CSV
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            {[
              {
                icon: '💰',
                title: 'Achats & Ventes',
                desc: 'Calcul automatique des plus-values (FIFO), distinction court-terme (<1 an) vs long-terme (>1 an)'
              },
              {
                icon: '✨',
                title: 'Swaps Crypto-Crypto',
                desc: '20+ colonnes incluant is_swap, swap_chain_id, original_cost_basis pour traçabilité complète'
              },
              {
                icon: '⛏️',
                title: 'Mining',
                desc: 'Pool utilisé (Ethermine, F2Pool, etc.), hashrate (MH/s, TH/s), quantité minée'
              },
              {
                icon: '🔒',
                title: 'Staking',
                desc: 'Platform (Binance, Kraken, Native), APY (%), duration, cooldown, type (locked/flexible)'
              },
              {
                icon: '📅',
                title: 'Périodes de Détention',
                desc: 'Jours de détention pour chaque position, type de gain (court-terme/long-terme)'
              },
              {
                icon: '🌍',
                title: 'Multi-Juridiction',
                desc: 'Support France (🇫🇷), USA (🇺🇸), UK (🇬🇧), Allemagne (🇩🇪) avec règles fiscales spécifiques'
              }
            ].map((feature, idx) => (
              <div key={idx} style={{
                padding: '16px',
                background: 'var(--bg)',
                borderRadius: '8px',
                display: 'flex',
                gap: '12px',
                alignItems: 'start'
              }}>
                <span style={{ fontSize: '28px', flexShrink: 0 }}>{feature.icon}</span>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '6px' }}>{feature.title}</div>
                  <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.6' }}>{feature.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Current Status */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: 'var(--accent)' }}>
            ✅ État Actuel (Version 4.4)
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ padding: '14px 16px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', borderLeft: '4px solid #10b981' }}>
              <strong style={{ color: '#10b981' }}>✅ Traçabilité Complète</strong>
              <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '6px 0 0', lineHeight: '1.6' }}>
                Toutes vos transactions (Achat, Vente, Swap, Mining, Staking) sont exportées avec TOUTES leurs métadonnées.
                Données prêtes pour comptables et fiscalistes.
              </p>
            </div>
            <div style={{ padding: '14px 16px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
              <strong style={{ color: '#3b82f6' }}>✅ Calculs Achats/Ventes</strong>
              <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '6px 0 0', lineHeight: '1.6' }}>
                Plus-values automatiquement calculées pour achats et ventes (FIFO), estimation d'impôt selon votre pays.
              </p>
            </div>
            <div style={{ padding: '14px 16px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
              <strong style={{ color: '#f59e0b' }}>⚠️ Swaps/Mining/Staking: Tracés, pas encore calculés</strong>
              <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '6px 0 0', lineHeight: '1.6' }}>
                Ces types sont <strong>inclus dans le CSV pour traçabilité</strong>, mais les calculs fiscaux automatiques
                arrivent en Q1 2025 (Phase 2-4 de la roadmap MiCA).
              </p>
            </div>
          </div>
        </div>

        {/* Roadmap */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: 'var(--accent)' }}>
            🎯 Roadmap MiCA (Q1-Q2 2025)
          </h3>
          <div style={{ display: 'grid', gap: '10px' }}>
            {[
              { phase: 'Phase 1', status: '✅', title: 'Traçabilité Données', date: 'Nov 2025', desc: 'TERMINÉ - Export CSV avec toutes métadonnées' },
              { phase: 'Phase 2', status: '🎯', title: 'Calcul Fiscal Swaps', date: 'Jan 2025', desc: 'Traiter comme vente FROM + achat TO' },
              { phase: 'Phase 3', status: '🎯', title: 'Calcul Fiscal Mining', date: 'Fév 2025', desc: 'Classifier comme revenu ordinaire' },
              { phase: 'Phase 4', status: '🎯', title: 'Calcul Fiscal Staking', date: 'Fév 2025', desc: 'Classifier comme revenu ordinaire' },
              { phase: 'Phase 5', status: '🎯', title: 'Conformité MiCA Complète', date: 'Mars-Avril 2025', desc: 'Distinction Gains/Revenus + Format officiel' }
            ].map((item, idx) => (
              <div key={idx} style={{
                padding: '12px 16px',
                background: 'var(--bg)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '13px'
              }}>
                <span style={{ fontSize: '20px' }}>{item.status}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                    {item.phase}: {item.title} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>({item.date})</span>
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: '12px' }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How to Use */}
        <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #8b5cf6' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '12px', fontWeight: 600 }}>
            🚀 Comment Utiliser l'Export Fiscal
          </h3>
          <ol style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: '1.7', paddingLeft: '20px', margin: 0 }}>
            <li style={{ marginBottom: '8px' }}>
              Dans le <strong>Portfolio Dashboard</strong>, cliquez sur le bouton <strong>"Export Fiscal"</strong>
            </li>
            <li style={{ marginBottom: '8px' }}>
              Sélectionnez votre <strong>pays</strong> (FR, US, UK, DE) et l'<strong>année fiscale</strong>
            </li>
            <li style={{ marginBottom: '8px' }}>
              Cliquez sur <strong>"Générer Rapport Fiscal CSV"</strong> - un fichier CSV sera téléchargé
            </li>
            <li style={{ marginBottom: '8px' }}>
              Ouvrez le CSV dans <strong>Excel/LibreOffice</strong> pour voir toutes vos transactions
            </li>
            <li>
              Partagez avec votre <strong>comptable/fiscaliste</strong> - toutes les données sont là !
            </li>
          </ol>
          <div style={{ marginTop: '16px', padding: '12px', background: 'var(--card)', borderRadius: '6px', fontSize: '13px' }}>
            <strong>📋 Note Important:</strong> Ce rapport est fourni à titre informatif. Consultez toujours un expert-comptable
            pour votre déclaration fiscale officielle.
          </div>
        </div>
      </div>
    </>
  );
}

// ==================== BOTS & DASHBOARDS ====================
function BotsDashboardsContent() {
  return (
    <>
      {/* AI Trading Bot - Social Sentiment */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Bot size={24} />
          🤖 AI Trading Bot - Sentiment Social
        </h2>

        <p style={{ fontSize: '15px', color: 'var(--muted)', marginBottom: '24px', lineHeight: '1.7' }}>
          Notre bot de trading alimenté par IA analyse le sentiment du marché en temps réel pour vous fournir des
          recommandations de trading intelligentes basées sur l'analyse sociale, technique et fondamentale.
        </p>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: 'var(--accent)' }}>
            📊 Fonctionnalités Principales
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            {[
              'Analyse sentiment social en temps réel (Twitter, Reddit, News)',
              'Score de sentiment pour chaque crypto (-100 à +100)',
              'Signaux d\'achat/vente basés sur l\'IA',
              'Détection tendances et momentum',
              'Alertes automatiques sur changements de sentiment',
              'Historique des prédictions et taux de réussite',
              'Recommandations personnalisées selon votre portfolio',
              'Analyse corrélation sentiment vs prix'
            ].map((feature, idx) => (
              <div key={idx} style={{
                padding: '14px 16px',
                background: 'var(--bg)',
                borderRadius: '8px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>✓</span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #8b5cf6' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '12px', fontWeight: 600 }}>
            🎯 Comment ça Marche ?
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: '1.7', marginBottom: '12px' }}>
            Le bot agrège des millions de points de données depuis les réseaux sociaux, les forums crypto, les actualités
            et les volumes de trading. Il utilise des modèles de machine learning pour détecter les patterns et prédire
            les mouvements de prix avant qu'ils ne se produisent.
          </p>
          <div style={{ fontSize: '13px', background: 'var(--card)', padding: '12px', borderRadius: '6px' }}>
            <strong>Exemple:</strong> Si le sentiment Twitter pour BTC passe de +20 à +75 avec une augmentation du volume,
            le bot peut suggérer un signal d'achat avec un niveau de confiance de 85%.
          </div>
        </div>
      </div>

      {/* Autopilot */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Sparkles size={24} />
          ✈️ Autopilot - Trading Automatisé
        </h2>

        <p style={{ fontSize: '15px', color: 'var(--muted)', marginBottom: '24px', lineHeight: '1.7' }}>
          Le mode Autopilot automatise vos décisions de trading en combinant analyse technique, sentiment social
          et gestion de risque. Laissez l'IA gérer votre portfolio selon vos paramètres de risque.
        </p>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: 'var(--accent)' }}>
            🚀 Capacités Autopilot
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            {[
              'Exécution automatique des trades selon stratégie définie',
              'Rééquilibrage automatique du portfolio',
              'Stop-loss et take-profit dynamiques',
              'Gestion taille positions selon risque',
              'Dollar-Cost Averaging (DCA) automatisé',
              'Protection contre volatilité extrême',
              'Backtesting de stratégies sur données historiques',
              'Rapports performance détaillés'
            ].map((feature, idx) => (
              <div key={idx} style={{
                padding: '14px 16px',
                background: 'var(--bg)',
                borderRadius: '8px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ color: '#10b981', fontWeight: 'bold' }}>✓</span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #10b981' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '12px', fontWeight: 600 }}>
            ⚙️ Paramètres Configurables
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <strong style={{ fontSize: '14px' }}>Profil de Risque</strong>
              <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0' }}>
                Conservateur, Modéré, Agressif
              </p>
            </div>
            <div>
              <strong style={{ fontSize: '14px' }}>Budget Allocation</strong>
              <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0' }}>
                % capital par trade
              </p>
            </div>
            <div>
              <strong style={{ fontSize: '14px' }}>Fréquence Trading</strong>
              <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0' }}>
                Day trading, Swing, Long-term
              </p>
            </div>
            <div>
              <strong style={{ fontSize: '14px' }}>Stop Loss</strong>
              <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0' }}>
                Perte max par trade
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Crypto.com Dashboard */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Activity size={24} />
          💎 Crypto.com Dashboard
        </h2>

        <p style={{ fontSize: '15px', color: 'var(--muted)', marginBottom: '24px', lineHeight: '1.7' }}>
          Suivez et gérez tous vos produits Crypto.com en un seul endroit : staking CRO, bots de trading,
          carte Crypto.com, produits Earn et plus encore.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '24px' }}>
          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>🔒</div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Staking CRO</h3>
            <ul style={{ fontSize: '14px', lineHeight: '1.7', paddingLeft: '20px', color: 'var(--muted)' }}>
              <li>Suivi des positions staking</li>
              <li>Calcul APY en temps réel</li>
              <li>Historique des récompenses</li>
              <li>Alertes fin de lockup</li>
            </ul>
          </div>

          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>💳</div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Carte Crypto.com</h3>
            <ul style={{ fontSize: '14px', lineHeight: '1.7', paddingLeft: '20px', color: 'var(--muted)' }}>
              <li>Suivi stake de carte</li>
              <li>Cashback accumulé</li>
              <li>Avantages tier actuel</li>
              <li>Calculateur upgrade tier</li>
            </ul>
          </div>

          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>🤖</div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Bots de Trading</h3>
            <ul style={{ fontSize: '14px', lineHeight: '1.7', paddingLeft: '20px', color: 'var(--muted)' }}>
              <li>Grid Trading Bot</li>
              <li>DCA Bot</li>
              <li>TWAP Bot</li>
              <li>Performance tracking</li>
            </ul>
          </div>

          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>📈</div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Produits Earn</h3>
            <ul style={{ fontSize: '14px', lineHeight: '1.7', paddingLeft: '20px', color: 'var(--muted)' }}>
              <li>Flexible Earn tracking</li>
              <li>Fixed Term positions</li>
              <li>Calcul intérêts composés</li>
              <li>Optimisation rendement</li>
            </ul>
          </div>
        </div>

        <div style={{ padding: '16px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '8px', borderLeft: '3px solid #6366f1' }}>
          <strong style={{ color: '#6366f1' }}>🔐 Connexion API Sécurisée:</strong> Connectez votre compte Crypto.com
          via API en lecture seule. Vos clés sont chiffrées et stockées localement.
        </div>
      </div>

      {/* KuCoin Dashboard */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Bot size={24} />
          🟢 KuCoin Dashboard
        </h2>

        <p style={{ fontSize: '15px', color: 'var(--muted)', marginBottom: '24px', lineHeight: '1.7' }}>
          Suivez vos bots de trading KuCoin, positions Earn, et statut VIP. Monitoring en temps réel de toutes
          vos activités KuCoin avec analytics détaillées.
        </p>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: 'var(--accent)' }}>
            🤖 Bots de Trading Supportés
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            {[
              'Classic Grid Bot - Trading dans une fourchette de prix',
              'Infinity Grid Bot - Grid sans limite supérieure',
              'DCA Bot - Dollar-Cost Averaging automatisé',
              'Margin Grid Bot - Grid avec effet de levier',
              'Smart Rebalance - Rééquilibrage automatique portfolio',
              'Futures Grid - Trading grid sur marchés futures'
            ].map((bot, idx) => (
              <div key={idx} style={{
                padding: '14px 16px',
                background: 'var(--bg)',
                borderRadius: '8px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>✓</span>
                <span>{bot}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', borderLeft: '3px solid #10b981' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>📊 Analytics en Temps Réel</h3>
            <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.6' }}>
              Suivez performance, profits, ROI, nombre de grids exécutées, et plus pour chaque bot actif.
            </p>
          </div>

          <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', borderLeft: '3px solid #6366f1' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>💰 Suivi Earn & VIP</h3>
            <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.6' }}>
              Monitoring positions Earn (Flexible, Locked), progression VIP, et avantages associés.
            </p>
          </div>

          <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', borderLeft: '3px solid #f59e0b' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>📈 Historique Performance</h3>
            <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.6' }}>
              Graphiques performance historique, analyse P&L, et comparaison entre différents bots.
            </p>
          </div>
        </div>

        <div style={{ padding: '16px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', borderLeft: '3px solid #f59e0b' }}>
          <strong style={{ color: '#f59e0b' }}>⚡ Synchronisation Auto:</strong> Les données se synchronisent
          automatiquement toutes les 5 minutes via l'API KuCoin pour un suivi en temps réel.
        </div>
      </div>
    </>
  );
}

// ==================== AUTOPILOT TRADING BOT ====================
function AutopilotTradingContent() {
  return (
    <>
      {/* Introduction */}
      <div className="card" style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: 'white',
        marginBottom: '24px'
      }}>
        <h1 style={{ fontSize: '32px', marginBottom: '16px', fontWeight: 800 }}>
          ✈️ Autopilot Trading Bot
        </h1>
        <p style={{ fontSize: '16px', lineHeight: '1.7', marginBottom: '20px', opacity: 0.95 }}>
          Le trading automatisé nouvelle génération. L'Autopilot exécute des stratégies DCA intelligentes 24/7,
          optimise vos achats selon la position du prix par rapport aux moyennes mobiles (MA200), et gère votre budget automatiquement.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 600
          }}>
            🤖 100% Automatisé
          </span>
          <span style={{
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 600
          }}>
            📊 3 Stratégies Disponibles
          </span>
          <span style={{
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 600
          }}>
            ⚡ Exécution 24/7
          </span>
          <span style={{
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 600
          }}>
            🔒 Dry-Run Mode
          </span>
        </div>
      </div>

      {/* Strategy 1: MA200 DCA */}
      <div className="card">
        <h2 style={{ fontSize: '26px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '32px' }}>📈</span>
          MA200 DCA Strategy - Value Investing Automatisé
        </h2>

        <p style={{ fontSize: '15px', color: 'var(--muted)', marginBottom: '24px', lineHeight: '1.7' }}>
          La stratégie ultime pour l'investissement value en crypto. Achète <strong>UNIQUEMENT</strong> quand
          le prix est proche de la moyenne mobile 200 périodes (MA200), une zone historiquement considérée
          comme un support fort. Plus le prix est bas, plus on achète.
        </p>

        <div style={{ padding: '20px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px', borderLeft: '4px solid #10b981', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 700, color: '#10b981' }}>
            💡 Philosophie : "Buy the Dip, Skip the Hype"
          </h3>
          <p style={{ fontSize: '14px', lineHeight: '1.7', marginBottom: '12px' }}>
            Au lieu d'acheter à n'importe quel prix (DCA classique), cette stratégie attend patiemment que
            le prix revienne près de sa MA200 avant d'acheter. C'est du <strong>value investing</strong> :
            acheter quand c'est historiquement "cheap", ignorer quand c'est trop cher.
          </p>
          <div style={{ fontSize: '13px', background: 'var(--card)', padding: '14px', borderRadius: '8px' }}>
            <strong>Exemple concret (BTC) :</strong><br/>
            • Prix actuel : $65,000<br/>
            • MA200 (1d) : $63,000<br/>
            • Distance : +3.2% au-dessus de MA200<br/>
            • ✅ <strong>Zone d'achat détectée !</strong> (dans le seuil de ±5%)<br/>
            • Montant d'achat : $150 (base $100 × multiplier 1.5)
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: 'var(--accent)' }}>
            🎯 Fonctionnalités Clés
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            {[
              {
                title: 'Achats Ciblés',
                desc: 'N\'achète que quand le prix est dans la zone MA200 (±5% par défaut)',
                icon: '🎯'
              },
              {
                title: 'Agressivité Variable',
                desc: 'Plus le prix est BAS par rapport à MA200, plus on achète (boost jusqu\'à 3x)',
                icon: '⚡'
              },
              {
                title: 'Support SMA & EMA',
                desc: 'Choisissez entre Simple MA (SMA200) ou Exponential MA (EMA200)',
                icon: '📊'
              },
              {
                title: 'Multi-Timeframe',
                desc: 'Calculez MA200 sur 1h, 4h, 1d, ou 1w selon votre stratégie',
                icon: '⏱️'
              },
              {
                title: 'Limites de Sécurité',
                desc: 'Max 3 achats/jour, 10/semaine, 60 min entre achats, réserve budgétaire',
                icon: '🔒'
              },
              {
                title: 'Confirmation RSI',
                desc: 'Optionnel : achète seulement si RSI < 40 (évite les faux signaux)',
                icon: '📉'
              },
              {
                title: 'Pause Automatique',
                desc: 'Peut se mettre en pause quand prix > MA200 (évite les ATH)',
                icon: '⏸️'
              }
            ].map((feature, idx) => (
              <div key={idx} style={{
                padding: '16px',
                background: 'var(--bg)',
                borderRadius: '10px',
                fontSize: '14px',
                border: '1px solid var(--border)'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>{feature.icon}</span>
                  <div>
                    <strong style={{ fontSize: '15px', color: 'var(--text)' }}>{feature.title}</strong>
                    <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0', lineHeight: '1.6' }}>
                      {feature.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '16px', fontWeight: 600 }}>
            ⚙️ Profils de Configuration
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <div style={{ padding: '16px', background: 'var(--card)', borderRadius: '8px', borderLeft: '3px solid #3b82f6' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px', color: '#3b82f6' }}>
                🛡️ Conservateur
              </h4>
              <ul style={{ fontSize: '13px', lineHeight: '1.8', paddingLeft: '18px', color: 'var(--muted)', margin: 0 }}>
                <li>Seuil MA200 : ±3%</li>
                <li>Multiplier : 1.5x</li>
                <li>Boost &lt; MA200 : 1.2x</li>
                <li>Max 2 achats/jour</li>
                <li>Réserve : 30%</li>
                <li>RSI requis : &lt; 35</li>
              </ul>
            </div>

            <div style={{ padding: '16px', background: 'var(--card)', borderRadius: '8px', borderLeft: '3px solid #10b981' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px', color: '#10b981' }}>
                ⚖️ Modéré (Défaut)
              </h4>
              <ul style={{ fontSize: '13px', lineHeight: '1.8', paddingLeft: '18px', color: 'var(--muted)', margin: 0 }}>
                <li>Seuil MA200 : ±5%</li>
                <li>Multiplier : 2.0x</li>
                <li>Boost &lt; MA200 : 1.5x</li>
                <li>Max 3 achats/jour</li>
                <li>Réserve : 20%</li>
                <li>RSI requis : &lt; 40</li>
              </ul>
            </div>

            <div style={{ padding: '16px', background: 'var(--card)', borderRadius: '8px', borderLeft: '3px solid #f59e0b' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px', color: '#f59e0b' }}>
                🔥 Agressif
              </h4>
              <ul style={{ fontSize: '13px', lineHeight: '1.8', paddingLeft: '18px', color: 'var(--muted)', margin: 0 }}>
                <li>Seuil MA200 : ±7%</li>
                <li>Multiplier : 3.0x</li>
                <li>Boost &lt; MA200 : 2.0x</li>
                <li>Max 5 achats/jour</li>
                <li>Réserve : 10%</li>
                <li>Pas de RSI requis</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Strategy 2: SmartDCA Enhanced */}
      <div className="card">
        <h2 style={{ fontSize: '26px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '32px' }}>🧠</span>
          SmartDCA Enhanced - DCA + MA200 Boost
        </h2>

        <p style={{ fontSize: '15px', color: 'var(--muted)', marginBottom: '24px', lineHeight: '1.7' }}>
          Le meilleur des deux mondes ! Cette stratégie combine la <strong>discipline du DCA régulier</strong>
          (achats hebdomadaires/mensuels) avec l'<strong>opportunisme de l'achat MA200</strong>. Vous maintenez
          votre routine d'investissement, mais vos achats deviennent automatiquement plus importants quand
          le prix est près de MA200.
        </p>

        <div style={{ padding: '20px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '12px', borderLeft: '4px solid #6366f1', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 700, color: '#6366f1' }}>
            💡 Philosophie : "DCA Régulier, Boost Opportuniste"
          </h3>
          <p style={{ fontSize: '14px', lineHeight: '1.7', marginBottom: '12px' }}>
            Contrairement au DCA classique qui achète toujours le même montant, SmartDCA Enhanced
            <strong> adapte intelligemment</strong> vos achats selon le contexte de marché. Votre DCA
            hebdomadaire de $100 devient automatiquement $300 quand le prix touche MA200 !
          </p>
          <div style={{ fontSize: '13px', background: 'var(--card)', padding: '14px', borderRadius: '8px' }}>
            <strong>Exemple concret (HBAR) :</strong><br/>
            • DCA programmé : Tous les lundis, $100<br/>
            • Aujourd'hui lundi : Prix HBAR à $0.095, MA200 à $0.092<br/>
            • Distance : +3.3% au-dessus de MA200<br/>
            • ✅ <strong>Boost MA200 appliqué !</strong><br/>
            • Montant d'achat : $100 × 1.5 (boost MA200) × 2.0 (below boost) = <strong>$300</strong>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: 'var(--accent)' }}>
            🎯 Fonctionnalités Clés
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            {[
              {
                title: 'DCA Programmé',
                desc: 'Choisissez votre fréquence : quotidien, hebdomadaire, bi-hebdomadaire, mensuel',
                icon: '📅'
              },
              {
                title: 'Dip Buying',
                desc: 'Achète plus lors des baisses de prix (dip de 5% = multiplier 2x, 20% = 5x)',
                icon: '📉'
              },
              {
                title: 'MA200 Boost (NOUVEAU)',
                desc: 'Multiplie automatiquement vos achats quand prix près de MA200',
                icon: '🚀'
              },
              {
                title: 'Cumul des Boosts',
                desc: 'Dip + MA200 se cumulent ! Un dip de 10% près de MA200 = boost massif',
                icon: '🔥'
              },
              {
                title: 'Fonds de Réserve',
                desc: 'Garde 20% de budget en réserve pour les grosses opportunités (dips)',
                icon: '💰'
              },
              {
                title: 'Pause RSI',
                desc: 'Pause automatique si marché surchauffé (RSI > 75)',
                icon: '⏸️'
              },
              {
                title: 'Protection Max Position',
                desc: 'Limite le montant total investi (ex: max $50k)',
                icon: '🛡️'
              }
            ].map((feature, idx) => (
              <div key={idx} style={{
                padding: '16px',
                background: 'var(--bg)',
                borderRadius: '10px',
                fontSize: '14px',
                border: '1px solid var(--border)'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>{feature.icon}</span>
                  <div>
                    <strong style={{ fontSize: '15px', color: 'var(--text)' }}>{feature.title}</strong>
                    <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0', lineHeight: '1.6' }}>
                      {feature.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '16px', fontWeight: 600 }}>
            📊 Exemple de Résultats (Backtest BTC 2020-2024)
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div style={{ padding: '14px', background: 'var(--card)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#ef4444', marginBottom: '4px' }}>+180%</div>
              <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Pure DCA</div>
            </div>
            <div style={{ padding: '14px', background: 'var(--card)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b', marginBottom: '4px' }}>+220%</div>
              <div style={{ fontSize: '13px', color: 'var(--muted)' }}>SmartDCA (dip)</div>
            </div>
            <div style={{ padding: '14px', background: 'var(--card)', borderRadius: '8px', textAlign: 'center', border: '2px solid #10b981' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981', marginBottom: '4px' }}>+310%</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#10b981' }}>SmartDCA Enhanced</div>
            </div>
            <div style={{ padding: '14px', background: 'var(--card)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#3b82f6', marginBottom: '4px' }}>+280%</div>
              <div style={{ fontSize: '13px', color: 'var(--muted)' }}>MA200 DCA</div>
            </div>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '12px', fontStyle: 'italic', textAlign: 'center' }}>
            *Résultats de backtest sur données historiques BTC 2020-2024, ne garantissent pas les performances futures.
          </p>
        </div>
      </div>

      {/* Strategy 3: Infinity Grid */}
      <div className="card">
        <h2 style={{ fontSize: '26px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '32px' }}>🔄</span>
          Infinity Grid Strategy - Scalping Automatisé
        </h2>

        <p style={{ fontSize: '15px', color: 'var(--muted)', marginBottom: '24px', lineHeight: '1.7' }}>
          Une stratégie de <strong>market-making passive</strong> qui profite de la volatilité. Le bot place
          automatiquement une grille d'ordres d'achat et de vente à différents niveaux de prix. Chaque fois
          que le prix oscille, le bot achète bas et vend haut, accumulant des profits incrementaux.
        </p>

        <div style={{ padding: '20px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '12px', borderLeft: '4px solid #f59e0b', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 700, color: '#f59e0b' }}>
            💡 Philosophie : "Profit from Volatility"
          </h3>
          <p style={{ fontSize: '14px', lineHeight: '1.7', marginBottom: '12px' }}>
            Contrairement au DCA qui accumule sur le long terme, le Grid Trading profite des fluctuations
            court-terme. Idéal pour les cryptos volatiles qui oscillent dans une fourchette de prix.
          </p>
          <div style={{ fontSize: '13px', background: 'var(--card)', padding: '14px', borderRadius: '8px' }}>
            <strong>Exemple concret (ETH) :</strong><br/>
            • Configuration : 20 grilles entre $2,000 - $2,500<br/>
            • Écart : $25 entre chaque grille<br/>
            • Le bot achète à $2,000, $2,025, $2,050...<br/>
            • Et vend à $2,025, $2,050, $2,075...<br/>
            • Profit par cycle : $25 (1.25% × 20 grilles = 25% de profit potentiel)
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: 'var(--accent)' }}>
            🎯 Fonctionnalités Clés
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            {[
              {
                title: 'Grilles Infinies',
                desc: 'Pas de limite supérieure - suit le prix à la hausse indéfiniment',
                icon: '∞'
              },
              {
                title: 'Stop-Loss Automatique',
                desc: 'Protection à la baisse avec limite de prix minimum configurable',
                icon: '🛡️'
              },
              {
                title: 'Rééquilibrage Auto',
                desc: 'Ajuste automatiquement la grille quand le prix sort de la zone',
                icon: '⚖️'
              },
              {
                title: 'Optimisation Continue',
                desc: 'Analyse la volatilité et ajuste l\'espacement des grilles',
                icon: '📊'
              },
              {
                title: 'Multi-Paire',
                desc: 'Lancez plusieurs grids simultanément sur différentes paires',
                icon: '🎯'
              }
            ].map((feature, idx) => (
              <div key={idx} style={{
                padding: '16px',
                background: 'var(--bg)',
                borderRadius: '10px',
                fontSize: '14px',
                border: '1px solid var(--border)'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>{feature.icon}</span>
                  <div>
                    <strong style={{ fontSize: '15px', color: 'var(--text)' }}>{feature.title}</strong>
                    <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0', lineHeight: '1.6' }}>
                      {feature.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Configuration Générale */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Settings size={24} />
          ⚙️ Configuration & Sécurité
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '24px' }}>
          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔒</div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>Dry-Run Mode</h3>
            <p style={{ fontSize: '14px', lineHeight: '1.7', color: 'var(--muted)' }}>
              Testez vos stratégies sans risque ! Le mode test simule tous les ordres sans toucher à vos fonds.
              Parfait pour valider votre configuration avant de passer en réel.
            </p>
          </div>

          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>Statistiques Temps Réel</h3>
            <p style={{ fontSize: '14px', lineHeight: '1.7', color: 'var(--muted)' }}>
              Dashboard complet avec : total dépensé, quantité accumulée, prix moyen, P&L, ROI, distance MA200,
              nombre d'achats, utilisation budget, et plus.
            </p>
          </div>

          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏱️</div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>Exécution 24/7</h3>
            <p style={{ fontSize: '14px', lineHeight: '1.7', color: 'var(--muted)' }}>
              Le bot tourne en continu sur nos serveurs. Vous n'avez jamais besoin de garder votre ordinateur allumé.
              Monitoring automatique et alertes par email.
            </p>
          </div>
        </div>

        <div style={{ padding: '20px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '12px', borderLeft: '4px solid #ef4444' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '12px', fontWeight: 600, color: '#ef4444' }}>
            ⚠️ Avertissements Importants
          </h3>
          <ul style={{ fontSize: '14px', lineHeight: '1.9', paddingLeft: '24px', margin: 0, color: 'var(--muted)' }}>
            <li><strong>Le trading comporte des risques.</strong> Ne investissez que ce que vous pouvez perdre.</li>
            <li><strong>Backtests ≠ Garantie.</strong> Les performances passées ne préjugent pas des futures.</li>
            <li><strong>Testez d'abord en Dry-Run.</strong> Validez votre stratégie sur au moins 2-4 semaines.</li>
            <li><strong>Commencez petit.</strong> Utilisez un budget réduit pour vos premiers bots réels.</li>
            <li><strong>Diversifiez.</strong> Ne mettez pas tout votre capital sur un seul bot/une seule crypto.</li>
            <li><strong>API Keys sécurisées.</strong> Utilisez des clés en lecture seule et trading uniquement (pas de withdrawal).</li>
          </ul>
        </div>
      </div>

      {/* Support Multi-Exchange */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Package size={24} />
          🌐 Support Multi-Exchange
        </h2>

        <p style={{ fontSize: '15px', color: 'var(--muted)', marginBottom: '24px', lineHeight: '1.7' }}>
          L'Autopilot Trading Bot supporte les exchanges suivants. Choisissez celui où vous avez vos fonds
          et connectez-le via API sécurisée.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          <div style={{ padding: '20px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', borderRadius: '12px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>🟡 Binance</h3>
            <p style={{ fontSize: '13px', opacity: 0.9, lineHeight: '1.6', margin: 0 }}>
              ✅ Disponible maintenant<br/>
              Le plus grand exchange crypto au monde. Frais bas, liquidité élevée.
            </p>
          </div>

          <div style={{ padding: '20px', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white', borderRadius: '12px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>💎 Crypto.com</h3>
            <p style={{ fontSize: '13px', opacity: 0.9, lineHeight: '1.6', margin: 0 }}>
              🚧 Bientôt disponible (Q1 2025)<br/>
              Support complet Crypto.com Exchange + CRO staking.
            </p>
          </div>

          <div style={{ padding: '20px', background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', color: 'white', borderRadius: '12px', opacity: 0.7 }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>🟢 KuCoin</h3>
            <p style={{ fontSize: '13px', opacity: 0.9, lineHeight: '1.6', margin: 0 }}>
              📋 En planification (Q2 2025)<br/>
              Accès aux altcoins exclusifs KuCoin.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

// ==================== AVANCÉ ====================
function AdvancedContent() {
  return (
    <>
      {/* Méthodes de Calcul */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Calculator size={24} />
          Méthodes de Calcul Expliquées
        </h2>

        <p style={{ fontSize: '15px', color: 'var(--muted)', marginBottom: '24px', lineHeight: '1.7' }}>
          Edouard.ai supporte trois méthodes standard de l'industrie pour calculer le coût de base et les plus-values.
          Choisissez celle qui correspond à votre juridiction fiscale ou stratégie de trading.
        </p>

        {/* FIFO */}
        <div style={{ marginBottom: '24px', padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #10b981' }}>
          <h3 style={{ fontSize: '20px', marginBottom: '12px', fontWeight: 600 }}>
            🟢 FIFO (First In, First Out)
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '16px', lineHeight: '1.7' }}>
            Les premiers tokens achetés sont les premiers vendus. C'est la <strong>méthode par défaut</strong> et la plus courante
            pour la fiscalité aux USA, Canada et la plupart des pays.
          </p>
          <div style={{ background: 'var(--card)', padding: '16px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px' }}>
            <strong>Exemple:</strong><br />
            Achat 1 BTC @ 30 000$ (1er Jan)<br />
            Achat 1 BTC @ 40 000$ (1er Fév)<br />
            Vente 1 BTC @ 50 000$ (1er Mar)<br />
            <br />
            <strong style={{ color: '#10b981' }}>Gain = 20 000$</strong> (vendu le 1er BTC @ 30k)
          </div>
        </div>

        {/* LIFO */}
        <div style={{ marginBottom: '24px', padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #6366f1' }}>
          <h3 style={{ fontSize: '20px', marginBottom: '12px', fontWeight: 600 }}>
            🔵 LIFO (Last In, First Out)
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '16px', lineHeight: '1.7' }}>
            Les tokens achetés le plus récemment sont vendus en premier. Utile pour <strong>l'optimisation fiscale</strong> dans certains scénarios,
            surtout pendant les marchés haussiers.
          </p>
          <div style={{ background: 'var(--card)', padding: '16px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px' }}>
            <strong>Exemple (même que ci-dessus):</strong><br />
            Achat 1 BTC @ 30 000$ (1er Jan)<br />
            Achat 1 BTC @ 40 000$ (1er Fév)<br />
            Vente 1 BTC @ 50 000$ (1er Mar)<br />
            <br />
            <strong style={{ color: '#6366f1' }}>Gain = 10 000$</strong> (vendu le dernier BTC @ 40k)
          </div>
        </div>

        {/* Coût Moyen */}
        <div style={{ marginBottom: '24px', padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #f59e0b' }}>
          <h3 style={{ fontSize: '20px', marginBottom: '12px', fontWeight: 600 }}>
            🟡 Coût Moyen
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '16px', lineHeight: '1.7' }}>
            Tous les achats sont moyennés ensemble pour calculer un coût de base moyen unique.
            Plus simple mais <strong>non accepté</strong> pour la fiscalité dans la plupart des pays.
          </p>
          <div style={{ background: 'var(--card)', padding: '16px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px' }}>
            <strong>Exemple (même que ci-dessus):</strong><br />
            Achat 1 BTC @ 30 000$ (1er Jan)<br />
            Achat 1 BTC @ 40 000$ (1er Fév)<br />
            Coût Moyen = (30k + 40k) / 2 = 35 000$<br />
            Vente 1 BTC @ 50 000$ (1er Mar)<br />
            <br />
            <strong style={{ color: '#f59e0b' }}>Gain = 15 000$</strong> (vendu @ coût moyen 35k)
          </div>
        </div>

        <div style={{ padding: '16px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '8px', borderLeft: '3px solid #8b5cf6' }}>
          <strong style={{ color: '#8b5cf6' }}>⚖️ Conseil Fiscal:</strong> Consultez un professionnel fiscal pour déterminer quelle méthode
          est requise ou optimale pour votre juridiction. FIFO est le choix le plus sûr par défaut.
        </div>
      </div>

      {/* Intégrations API */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.ExternalLink size={24} />
          Intégrations API
        </h2>

        {/* CoinGecko */}
        <div style={{ marginBottom: '24px', padding: '20px', background: 'var(--bg)', borderRadius: '12px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
            🦎 API CoinGecko
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '12px', lineHeight: '1.7' }}>
            Données de prix en temps réel pour plus de 10 000 cryptomonnaies. Tier gratuit avec actualisation auto toutes les 60 secondes.
          </p>
          <div style={{ fontSize: '13px' }}>
            <strong>Fonctionnalités:</strong> Prix en direct, variation 24h, données marché, prix historiques, suivi ATH/ATL
          </div>
        </div>

        {/* KuCoin */}
        <div style={{ marginBottom: '24px', padding: '20px', background: 'var(--bg)', borderRadius: '12px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
            🤖 Bots de Trading KuCoin
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '12px', lineHeight: '1.7' }}>
            Connectez votre compte KuCoin via API pour suivre les bots Grid, DCA et Infinity Grid en temps réel.
          </p>
          <div style={{ padding: '12px', background: 'var(--card)', borderRadius: '6px', fontSize: '13px' }}>
            <strong>Bots Supportés:</strong> Classic Grid, Infinity Grid, DCA, Margin Grid, Smart Rebalance
          </div>
        </div>

        {/* Crypto.com */}
        <div style={{ marginBottom: '24px', padding: '20px', background: 'var(--bg)', borderRadius: '12px' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
            💎 Crypto.com (Feuille de Route)
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '12px', lineHeight: '1.7' }}>
            Intégration prévue pour staking, bots de trading et suivi carte CRO.
          </p>
          <div style={{ padding: '12px', background: 'var(--card)', borderRadius: '6px', fontSize: '13px' }}>
            <strong>Prévu:</strong> Staking CRO, Stake Carte, Produits Earn, Bots Trading, Calculateur APY
          </div>
        </div>
      </div>

      {/* Conseils Performance */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Zap size={24} />
          Conseils de Performance
        </h2>

        <div style={{ display: 'grid', gap: '16px' }}>
          {[
            {
              title: '🚀 Utiliser Cache Prix',
              desc: 'Exportez votre portfolio pour sauvegarder les snapshots prix. L\'import restaure les prix instantanément sans appels API.'
            },
            {
              title: '📊 Mode Compact',
              desc: 'Activez le mode compact (touche C) pour afficher plus de données à l\'écran et réduire le défilement.'
            },
            {
              title: '⚡ Raccourcis Clavier',
              desc: 'Maîtrisez les raccourcis clavier (⌘K) pour une navigation et des actions 10x plus rapides.'
            },
            {
              title: '🎯 Filtrer Intelligemment',
              desc: 'Utilisez les filtres portfolio pour vous concentrer sur des tokens, tags ou plages P&L spécifiques.'
            },
            {
              title: '💾 Exporter Régulièrement',
              desc: 'Sauvegardez votre portfolio hebdomadairement via export JSON pour éviter la perte de données.'
            },
            {
              title: '📱 Installation PWA',
              desc: 'Installez en PWA pour chargement plus rapide, accès hors ligne et sensation d\'app native.'
            }
          ].map((tip, idx) => (
            <div key={idx} style={{
              padding: '16px 20px',
              background: 'var(--bg)',
              borderRadius: '8px',
              borderLeft: '3px solid var(--accent)'
            }}>
              <h3 style={{ fontSize: '16px', marginBottom: '8px', fontWeight: 600 }}>{tip.title}</h3>
              <p style={{ fontSize: '14px', color: 'var(--muted)', margin: 0, lineHeight: '1.6' }}>{tip.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ==================== FEUILLE DE ROUTE ====================
function RoadmapContent() {
  return (
    <>
      {/* Vision */}
      <div className="card" style={{
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
        borderLeft: '4px solid var(--accent)'
      }}>
        <h2 style={{ fontSize: '24px', marginBottom: '16px', fontWeight: 700 }}>
          🚀 Vision Produit
        </h2>
        <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
          Edouard.ai vise à devenir la plateforme de gestion de portfolio crypto la plus <strong>complète</strong> et
          <strong> conviviale</strong>. Notre vision combine analytiques puissantes et simplicité.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ padding: '8px 16px', background: 'var(--accent)', color: 'white', borderRadius: '20px', fontSize: '14px', fontWeight: 500 }}>
            ✨ Analytiques Professionnelles
          </span>
          <span style={{ padding: '8px 16px', background: 'var(--accent)', color: 'white', borderRadius: '20px', fontSize: '14px', fontWeight: 500 }}>
            🎯 Interface Conviviale
          </span>
          <span style={{ padding: '8px 16px', background: 'var(--accent)', color: 'white', borderRadius: '20px', fontSize: '14px', fontWeight: 500 }}>
            🔐 Approche Confidentialité d'Abord
          </span>
        </div>
      </div>

      {/* Statut Actuel */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.CheckCircle size={24} />
          ✅ Disponible Maintenant (V2.8)
        </h2>

        <div style={{ display: 'grid', gap: '12px' }}>
          {[
            'Gestion portfolio complète avec FIFO/LIFO/Coût Moyen',
            'Suivi prix temps réel (API CoinGecko)',
            'Visualisations avancées (20+ graphiques et analytiques)',
            'Suivi bots KuCoin (Grid, DCA, Infinity Grid)',
            'Support multi-portfolios',
            'Export/Import: JSON, CSV, QR Code',
            'Design responsive optimisé mobile',
            'Mode sombre avec transitions fluides',
            'Suggestions Intelligentes (insights alimentés IA)',
            'Analyse Risque et suivi Drawdown',
            'Analyse ATH/ATL (période 2 ans)',
            'Système d\'alertes avec déclencheurs prix',
            'Support PWA (app installable)',
            'Raccourcis clavier et Palette Commandes',
            'Suivi Prix Cible avec visualisation progression'
          ].map((feature, idx) => (
            <div key={idx} style={{
              padding: '14px 16px',
              background: 'rgba(16, 185, 129, 0.05)',
              borderRadius: '8px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              borderLeft: '3px solid #10b981'
            }}>
              <span style={{ color: '#10b981', fontWeight: 'bold', fontSize: '16px' }}>✓</span>
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Fonctionnalités à Venir */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Clock size={24} />
          🔮 Bientôt Disponible (Q1 2026)
        </h2>

        <div style={{ display: 'grid', gap: '20px' }}>
          {/* Intégration Crypto.com */}
          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #002D74' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 600 }}>
              💎 Intégration Complète Crypto.com
            </h3>
            <ul style={{ fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '12px' }}>
              <li>Dashboard staking CRO (Lockup, Soft Lockup, DeFi)</li>
              <li>Suivi stake carte avec visualisation avantages tier</li>
              <li>Support bots trading (Grid, DCA, TWAP, Arbitrage Funding)</li>
              <li>Suivi produits Earn avec calculateur APY</li>
              <li>Vue portfolio combinée (Trading + Staking + Carte)</li>
              <li>Timeline récompenses et optimisation APY</li>
            </ul>
            <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
              <strong>Chronologie:</strong> 3-4 mois développement • <strong>Complexité:</strong> Élevée
            </div>
          </div>

          {/* Fonctionnalités IA */}
          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #8b5cf6' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 600 }}>
              🤖 Fonctionnalités Alimentées par IA
            </h3>
            <ul style={{ fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '12px' }}>
              <li>Recommandations rééquilibrage portfolio</li>
              <li>Prédiction score risque basée sur concentration</li>
              <li>Suggestions points d\'entrée/sortie intelligents</li>
              <li>Intégration analyse sentiment marché</li>
              <li>Alertes automatisées tax loss harvesting</li>
              <li>Parcours apprentissage personnalisé dans Academy</li>
            </ul>
            <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
              <strong>Chronologie:</strong> 6-8 semaines • <strong>Complexité:</strong> Moyenne
            </div>
          </div>

          {/* Fonctionnalités Sociales */}
          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #10b981' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 600 }}>
              👥 Social & Comparaison
            </h3>
            <ul style={{ fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '12px' }}>
              <li>Classements performance portfolio anonymes</li>
              <li>Comparez votre stratégie vs moyenne communauté</li>
              <li>Partage stats portfolio (opt-in, confidentialité d\'abord)</li>
              <li>Templates stratégie publics (copier portfolios réussis)</li>
              <li>Vitrine achievements et badges</li>
            </ul>
            <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
              <strong>Chronologie:</strong> 4-5 semaines • <strong>Complexité:</strong> Moyenne
            </div>
          </div>

          {/* Graphiques Avancés */}
          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '12px', borderLeft: '4px solid #f59e0b' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: 600 }}>
              📊 Graphiques Avancés
            </h3>
            <ul style={{ fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '12px' }}>
              <li>Graphiques interactifs style TradingView</li>
              <li>Indicateurs techniques (RSI, MACD, Bandes Bollinger)</li>
              <li>Plages temporelles personnalisées et contrôles zoom</li>
              <li>Reconnaissance patterns chandeliers</li>
              <li>Analyse volume</li>
              <li>Comparaisons overlay multi-tokens</li>
            </ul>
            <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
              <strong>Chronologie:</strong> 3-4 semaines • <strong>Complexité:</strong> Moyenne
            </div>
          </div>
        </div>
      </div>

      {/* Vision Long Terme */}
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.TrendingUp size={24} />
          🌟 Vision Long Terme (2026-2027)
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          {[
            { icon: '🌐', title: 'Support Multi-Exchanges', desc: 'Intégrations Binance, Coinbase, Bybit, OKX' },
            { icon: '💹', title: 'Intégration DeFi', desc: 'Suivi yield farming, pools liquidité, positions prêt' },
            { icon: '🎮', title: 'Portfolio NFT', desc: 'Suivi collection NFT avec analyse floor price' },
            { icon: '📈', title: 'Trading Automatisé', desc: 'Copy-trading, automatisation stratégie, backtesting' },
            { icon: '🔔', title: 'Notifications Avancées', desc: 'Alertes Telegram, Discord, Email, SMS' },
            { icon: '🌍', title: 'Multi-Devises', desc: 'Support EUR, GBP, JPY, devises locales' },
            { icon: '📱', title: 'Apps Mobiles', desc: 'Applications natives iOS et Android' },
            { icon: '🔐', title: 'Sync Cloud (Optionnel)', desc: 'Backup cloud chiffré bout-en-bout' }
          ].map((vision, idx) => (
            <div key={idx} style={{
              padding: '20px',
              background: 'var(--bg)',
              borderRadius: '12px',
              border: '1px solid var(--border)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>{vision.icon}</div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>{vision.title}</h3>
              <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0, lineHeight: '1.6' }}>{vision.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contribution */}
      <div className="card" style={{
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
        borderLeft: '4px solid #10b981'
      }}>
        <h2 style={{ fontSize: '24px', marginBottom: '16px', fontWeight: 700 }}>
          💡 Demandes de Fonctionnalités & Feedback
        </h2>
        <p style={{ fontSize: '15px', lineHeight: '1.8', marginBottom: '16px' }}>
          Vous avez une idée ? Nous aimerions vous entendre ! Edouard.ai est construit avec le feedback utilisateur au cœur.
          Soumettez vos demandes de fonctionnalités, signalez bugs, ou partagez votre expérience.
        </p>
        <div style={{ fontSize: '14px', color: 'var(--muted)' }}>
          <strong>Contact:</strong> feedback@edouard.ai • <strong>GitHub:</strong> github.com/edouardai/issues
        </div>
      </div>
    </>
  );
}

// ==================== FAQ ====================
function FAQContent({ expandedSection, toggleSection }: { expandedSection: string | null; toggleSection: (section: string) => void }) {
  const faqs = [
    {
      id: 'faq1',
      question: 'Mes données sont-elles sécurisées ? Où sont-elles stockées ?',
      answer: 'Oui, 100% sécurisées ! Toutes vos données sont stockées localement dans votre navigateur via localStorage. Nous n\'avons aucun serveur backend, base de données ou tracking. Vos données portfolio ne quittent jamais votre appareil sauf si vous les exportez explicitement. Les clés API (si utilisées) sont chiffrées et stockées localement.'
    },
    {
      id: 'faq2',
      question: 'Dois-je créer un compte ?',
      answer: 'Aucun compte requis ! Edouard.ai est une application axée confidentialité. Ouvrez simplement l\'app et commencez à ajouter votre portfolio. Toutes les fonctionnalités sont disponibles immédiatement sans inscription.'
    },
    {
      id: 'faq3',
      question: 'Comment sauvegarder mon portfolio ?',
      answer: 'Allez dans Portfolio Dashboard → Cliquez "Export JSON". Cela télécharge une sauvegarde complète incluant tous les tokens, positions, prix, alertes et paramètres. Vous pouvez importer ce fichier à tout moment ou le transférer sur un autre appareil via QR code.'
    },
    {
      id: 'faq4',
      question: 'Quelle méthode de calcul dois-je utiliser ?',
      answer: 'FIFO (First In, First Out) est la méthode par défaut et la plus largement acceptée pour la fiscalité aux USA, Canada et la plupart des pays. Consultez un professionnel fiscal pour confirmer la méthode requise pour votre juridiction. Vous pouvez changer de méthode à tout moment dans Paramètres.'
    },
    {
      id: 'faq5',
      question: 'À quelle fréquence les prix se mettent-ils à jour ?',
      answer: 'Les prix sont récupérés depuis l\'API CoinGecko et s\'actualisent automatiquement toutes les 60 secondes. Vous pouvez aussi actualiser manuellement en cliquant le bouton rafraîchir ou en appuyant sur "R" sur desktop.'
    },
    {
      id: 'faq6',
      question: 'Puis-je utiliser ceci hors ligne ?',
      answer: 'Oui ! Installez Edouard.ai en PWA (Progressive Web App) pour un accès hors ligne. Vos données sont stockées localement, vous pouvez donc voir votre portfolio à tout moment. Les mises à jour de prix nécessitent une connexion internet.'
    },
    {
      id: 'faq7',
      question: 'Comment transférer mon portfolio sur mobile ?',
      answer: 'Exportez votre portfolio via QR Code sur desktop, puis importez-le sur mobile en scannant le QR code. Alternativement, exportez en fichier JSON et importez sur l\'autre appareil.'
    },
    {
      id: 'faq8',
      question: 'Y a-t-il une limite sur le nombre de tokens ?',
      answer: 'Aucune limite ! Ajoutez autant de tokens et positions que vous voulez. L\'app est optimisée pour gérer efficacement de grands portfolios.'
    },
    {
      id: 'faq9',
      question: 'Puis-je suivre plusieurs portfolios ?',
      answer: 'Oui ! Utilisez la fonctionnalité Multi-Portfolio pour créer des portfolios illimités. Parfait pour séparer holdings long terme, trading, DeFi, etc.'
    },
    {
      id: 'faq10',
      question: 'Comment connecter mon compte KuCoin/Crypto.com ?',
      answer: 'Allez dans la page dashboard respective (KuCoin Dashboard ou Crypto.com Dashboard) et suivez les instructions de configuration API. Vos clés API sont chiffrées et stockées localement, jamais envoyées à aucun serveur.'
    },
    {
      id: 'faq11',
      question: 'Quelle est la différence entre le mode Simple et Expert ?',
      answer: 'Le Mode Simple affiche 7 pages essentielles (Portfolio, Visualizations, Academy, Guide, Wallet Tracker, Changelog, Settings) avec une interface épurée parfaite pour les débutants. Le Mode Expert débloque 6 pages supplémentaires (Visualizations 2, Analyses Intelligentes, AI Trading Bot, Autopilot, Crypto.com Dashboard, KuCoin Dashboard, Logs) et affiche FIFO/LIFO en permanence dans la TopBar. Changez de mode via le bouton TopBar ou Settings → Apparence. Votre choix est sauvegardé automatiquement.'
    },
    {
      id: 'faq12',
      question: 'Comment fonctionne Seed Vault pour protéger mes seed phrases ?',
      answer: 'Seed Vault offre 3 niveaux de protection : (1) Chiffrement AES-256-GCM de niveau militaire avec dérivation de clé Argon2id pour protéger contre les attaques par force brute. (2) Stéganographie LSB pour cacher vos données chiffrées dans des images PNG/BMP ordinaires (3 patterns : Sequential, Random, Spread). (3) 100% client-side : vos seed phrases ne quittent JAMAIS votre appareil. Aucune transmission serveur, tout reste local. Workflow simple : Chiffrer → Télécharger JSON → Optionnellement cacher dans image. Pour récupérer : Téléverser JSON/image → Entrer mot de passe → Déchiffrer. ATTENTION : Si vous oubliez votre mot de passe, la récupération est impossible !'
    },
    {
      id: 'faq13',
      question: 'Comment utiliser Bitcoin Cycles pour comprendre les patterns de marché ?',
      answer: 'Bitcoin Cycles visualise les patterns fractals du Bitcoin sur des cycles de ~4 ans liés aux halvings. Le graphique affiche 4 phases de marché : (1) Bear Market (12 mois) - Capitulation, prix sous le coût de minage, accumulation des "smart money". (2) Accumulation (12 mois) - Consolidation, faible volatilité, période idéale pour accumuler. (3) Markup (18 mois) - Bull run, croissance exponentielle, nouveaux ATH, FOMO. (4) Distribution (6 mois) - Euphorie maximale, prix >5x coût de minage, prise de profits. Le ratio Prix/Coût de minage (ligne orange) est un indicateur clé : <1.2x = Bear, >5x = Distribution. Utilisez le sélecteur de période (1Y/4Y/Tout) pour analyser différentes échelles de temps. Les 4 cycles historiques montrent des ROI décroissants : #1 (8,746%), #2 (9,650%), #3 (2,056%), #4 (868% projeté). Mode Expert uniquement.'
    }
  ];

  return (
    <>
      <div className="card">
        <h2 style={{ fontSize: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.AlertCircle size={24} />
          Questions Fréquemment Posées
        </h2>

        <div style={{ display: 'grid', gap: '12px' }}>
          {faqs.map(faq => (
            <div
              key={faq.id}
              style={{
                padding: '16px 20px',
                background: expandedSection === faq.id ? 'var(--bg)' : 'transparent',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => toggleSection(faq.id)}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 600,
                fontSize: '15px'
              }}>
                <span>{faq.question}</span>
                <span style={{ fontSize: '20px', transition: 'transform 0.2s', transform: expandedSection === faq.id ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </div>
              {expandedSection === faq.id && (
                <p style={{
                  marginTop: '12px',
                  fontSize: '14px',
                  color: 'var(--muted)',
                  lineHeight: '1.7',
                  paddingTop: '12px',
                  borderTop: '1px solid var(--border)'
                }}>
                  {faq.answer}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Encore des Questions */}
      <div className="card" style={{
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(79, 70, 229, 0.1) 100%)',
        borderLeft: '4px solid #6366f1',
        textAlign: 'center'
      }}>
        <h3 style={{ fontSize: '20px', marginBottom: '12px', fontWeight: 600 }}>
          Encore des Questions ?
        </h3>
        <p style={{ fontSize: '15px', color: 'var(--muted)', marginBottom: '16px', lineHeight: '1.7' }}>
          Consultez l'<strong>Academy</strong> pour des tutoriels vidéo ou contactez notre équipe support.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button style={{
            padding: '12px 24px',
            background: 'var(--accent)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}>
            📧 Contacter Support
          </button>
          <button style={{
            padding: '12px 24px',
            background: 'transparent',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}>
            🎓 Visiter Academy
          </button>
        </div>
      </div>
    </>
  );
}
